import React, { useEffect, useState, useCallback } from 'react'
import { CSVLink } from 'react-csv';

import * as echarts from 'echarts';
import ReactEcharts from "echarts-for-react";
import { getUserAnalytics } from '../../api/user'
import { getUserInviteSplits, revenueShared } from '../../api/splits'
import { snakeToCamel } from '../AdminDashboard/AdminEditRelease'
import { useQuery } from '@tanstack/react-query'
import { Shimmer } from 'react-shimmer'
import { useDebounce } from '../AdminDashboard/AdminAnalytics'
import { getReleaseStats } from '../../api/releaseStats'
import ReleaseStatsTable from './release_table'
import TopList from './TopList'
import TopPlatformList from './TopPlatformList'

const Analytics = () => {

  const uid = localStorage.getItem('uid')
  const [labels, setLabels] = useState([])
  const [reports, setReports] = useState([])
  const [values, setValues] = useState([])
  const [filteredValues, setFilteredValues] = useState([])
  const [yearFilter, setYearFilter] = useState([])
  const [year, setYear] = useState('')
  const [topPlatforms, setTopPlatforms] = useState([])
  const [topCountries, setTopCountries] = useState([])
  const [topReleases, setTopReleases] = useState([])
  const [releases, setReleases] = useState([])
  const [releaseTracks, setReleaseTracks] = useState([])
  const [previewTitle, setPreviewTitle] = useState('')
  const [balance, setBalance] = useState(0)
  const [totalPaid, setTotalPaid] = useState(0)
  const [lastMonthRevenue, setLastMonthRevenue] = useState(0)
  const [platformsData, setPlatformsData] = useState([])
  const [hasData, setHasData] = useState(false)
  const [searchQuery, setSearchQuery] = useState('');
  const [totalInviteSplitRevenue, setTotalInviteSplitRevenue] = useState(0);
  const [inviteSplitsList, setInviteSplitsList] = useState([]);
  const localData = localStorage.getItem(`userAnalytics_${uid}`)

  // Helper functions that don't set state
  const getUniqueYears = useCallback((array) => {
    const uniqueYears = new Set();
    array.forEach(entry => {
      if (entry && entry.monthYear) {
        const [month, year] = entry.monthYear.split('/');
        uniqueYears.add(year);
      }
    });
    return Array.from(uniqueYears).sort();
  }, []);


  const { data: inviteSplits, isLoading: inviteSplitsLoading } = useQuery({
    queryKey: ["invite_splits", uid],
    queryFn: () => getUserInviteSplits(uid),
    select: (data) => {
      // Handle both possible response structures
      if (Array.isArray(data)) {
        return data;
      } else if (data && data.invite_splits) {
        return data.invite_splits;
      } else {
        return [];
      }
    }
  })


  useEffect(() => {
    if (inviteSplits && inviteSplits.length > 0) {
      // Calculate total revenue from invite splits
      const totalRevenue = inviteSplits.reduce((total, split) => total + split.revenue_share, 0);
      setTotalInviteSplitRevenue(totalRevenue.toFixed(2));

      // Sort invite splits by revenue share (highest first)
      const sortedSplits = [...inviteSplits].sort((a, b) => b.revenue_share - a.revenue_share);
      setInviteSplitsList(sortedSplits);
    } else {
      setTotalInviteSplitRevenue(0);
      setInviteSplitsList([]);
    }
  }, [inviteSplits]);

  const getFilteredByYear = useCallback((array, selectedYear) => {
    if (!array || array.length === 0 || !selectedYear) return [];

    // Filter by year and sort by month
    const filtered = array.filter(entry => {
      if (!entry || !entry.monthYear) return false;
      const [month, year] = entry.monthYear.split('/');
      return year === selectedYear;
    });

    // Sort by month (numerically)
    return filtered.sort((a, b) => {
      const [monthA] = a.monthYear.split('/');
      const [monthB] = b.monthYear.split('/');
      return parseInt(monthA) - parseInt(monthB);
    });
  }, []);

  // Process analytics data function
  const processAnalyticsData = useCallback((analytics) => {
    if (!analytics) return;

    try {
      const reportsData = analytics.reports?.map(report => snakeToCamel(report)) || [];
      const sortingData = analytics.sorting?.map(channel => snakeToCamel(channel)) || [];

      let currentTopPlatforms = [];
      let currentTopCountries = [];
      let currentTopReleases = [];

      if (sortingData && sortingData.length > 0) {
        currentTopPlatforms = sortingData[0].channels.map(channelI => ({ ...channelI, channel: channelI.platform || channelI.channel })) || [];
        currentTopCountries = sortingData[0].countries || [];
        currentTopReleases = sortingData[0].releases || [];
      }

      setValues(reportsData);

      const years = getUniqueYears(reportsData);
      setYearFilter(years);

      if (reportsData.length > 0) {
        const latestYear = years[years.length - 1];
        const filtered = getFilteredByYear(reportsData, latestYear);
        setFilteredValues(filtered);
        setYear(latestYear);
        setLastMonthRevenue(reportsData[reportsData.length - 1]?.totalPrice?.toFixed(2) || 0);
      } else {
        setLastMonthRevenue(0);
        setFilteredValues([]);
      }

      setTopPlatforms(currentTopPlatforms.sort((a, b) => (b.totalRevenue || b.revenue) - (a.totalRevenue || b.revenue)).slice(0, 10));
      setTopCountries(currentTopCountries.sort((a, b) => (b.totalRevenue || b.revenue) - (a.totalRevenue || a.revenue)).slice(0, 10));
      setTopReleases(currentTopReleases.sort((a, b) => (b.totalRevenue || b.revenue) - (a.totalRevenue || a.revenue)).slice(0, 10));

      let mapped = currentTopCountries.sort((a, b) => (b.totalRevenue || b.revenue) - (a.totalRevenue || a.revenue)).slice(0, 10)
      mapped = mapped.map((country) => {
        return {
          country: country.country || country.territory,
          streams: country.streams,
          totalRevenue: country.revenue || country.totalRevenue,
        }
      })
      let mapped_release = currentTopReleases.sort((a, b) => (b.totalRevenue || b.revenue) - (a.totalRevenue || a.revenue)).slice(0, 10)
      mapped_release = mapped_release.map(m => {
        return {
          release: m.title || m.release,
          streams: m.streams,
          totalRevenue: m.revenue || m.totalRevenue
        }
      })
      setTopReleases(mapped_release);
      setTopCountries(mapped);
      if (sortingData && sortingData.length > 0) {
        const currentPlatformsDataRaw = sortingData[0]?.channels?.map(platform => {
          let platformName = platform.channel?.toLowerCase() || platform.platform?.toLowerCase();
        
          if (platformName === 'snapshat') platformName = 'Snap';
          else if (platformName === 'apple music / itunes' || platformName === 'itunes' || platformName === 'itunes match') platformName = 'Apple Music';
          else if (platformName.includes('facebook')) platformName = 'Facebook';
          else if (platformName.includes('amazon')) platformName = 'Amazon';
          else if (platformName.includes('tiktok')) platformName = 'TikTok';
          else if (platformName.includes('spotify')) platformName = 'Spotify';
          else platformName = platform.channel || platform.platform;
        
          return {
            value: Number((platform.totalRevenue || platform.revenue) || 0),
            name: platformName
          };
        }) || [];
        
        // Accumulate values by name
        const accumulatedPlatforms = Object.values(
          currentPlatformsDataRaw.reduce((acc, { name, value }) => {
            if (!acc[name]) acc[name] = { name, value: 0 };
            acc[name].value += value;
            return acc;
          }, {})
        );
        
        // Optionally, toFixed(0) if you want integer string values
        const currentPlatformsData = accumulatedPlatforms.map(item => ({
          name: item.name,
          value: item.value.toFixed(0)
        }));
        
        setPlatformsData(currentPlatformsData)
      } else {
        setPlatformsData([])
      }

      setTotalPaid(analytics.total_paid !== undefined ? analytics.total_paid.toFixed(2) : 0)
      setBalance(analytics.balance !== undefined ? analytics.balance.toFixed(2) : 0)
      setHasData(true);
    } catch (error) {
      console.error('Error processing analytics data:', error);
    }
  }, [getUniqueYears, getFilteredByYear]);

  // Load data from localStorage on component mount
  useEffect(() => {
    try {
      if (localData) {
        processAnalyticsData(JSON.parse(localData));
      }
    } catch (error) {
      console.error('Error loading cached analytics data:', error);
    }
  }, [localData, processAnalyticsData]);

  const { isLoading, isError, data } = useQuery({
    queryKey: ['userAnalytics', uid],
    queryFn: () => getUserAnalytics(uid),
  });
  const { data: revenue_shared_by, isLoading: revenueSharedLoading } = useQuery({
    queryKey: ['rev_shared', uid],
    queryFn: async () => await revenueShared(uid),
    select: (data) => data.data.filter(d => d.user_id != uid)
  })

  // Save to localStorage and process when API data changes
  useEffect(() => {
    if (data) {
      try {
        localStorage.setItem(`userAnalytics_${uid}`, JSON.stringify(data));
        processAnalyticsData(data);
      } catch (error) {
        console.error('Error saving analytics data to localStorage:', error);
      }
    }
  }, [data, uid, processAnalyticsData]);

  // Handle year filter change
  const handleYearChange = useCallback((selectedYear) => {
    if (!selectedYear || selectedYear === year) return;

    setYear(selectedYear);

    // Make sure we're using the most up-to-date values array
    const filtered = getFilteredByYear(values, selectedYear);
    setFilteredValues(filtered);

    // Force re-render of chart data
    setTimeout(() => {
      const chartElement = document.getElementById('user-revenue-chart');
      if (chartElement) {
        chartElement.style.opacity = '0.5';
        setTimeout(() => {
          chartElement.style.opacity = '1';
        }, 100);
      }
    }, 0);
  }, [values, year, getFilteredByYear]);

  /////// RELEASES PAGINATION ////////


  /////////// PAGINATION ///////////
  ///////////// FLATEN RELEASES ///////////////

  const flattenReleases = ''
  // releases.reduce((accumulator, item) => {
  //   const { title, tracks } = item;
  //   const flatTracks = tracks.map(track => ({
  //     release: title,
  //     track: track.title,
  //     streams: track.streams,
  //     revenue: track.revenue
  //   }));
  //   return [...accumulator, ...flatTracks];
  // }, []);

  const option = {
    color: ['#377099'],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985'
        }
      }
    },
    toolbox: {
      feature: {
        saveAsImage: {}
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: [
      {
        type: 'category',
        boundaryGap: false,
        data: filteredValues.map(item => item?.monthYear || '')
      }
    ],
    yAxis: [
      {
        type: 'value'
      }
    ],
    series: [
      {
        name: 'Revenue',
        type: 'line',
        stack: 'Total',
        smooth: true,
        lineStyle: {
          width: 0
        },
        showSymbol: false,
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: '#377099'
            },
            {
              offset: 1,
              color: '#fff'
            }
          ]),
        },
        emphasis: {
          focus: 'series'
        },
        data: filteredValues.map(item => item?.totalPrice?.toFixed(0) || 0)
      }
    ]
  };

  const option1 = {
    tooltip: {
      trigger: 'item'
    },
    legend: {
      left: 'center',
    },
    series: [
      {
        top: '20%',
        name: 'Revenue',
        type: 'pie',
        radius: ['50%', '100%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 15,
            fontWeight: 'bold'
          }
        },
        labelLine: {
          show: false
        },
        data: platformsData
      }
    ]
  };

  // Initialize Bootstrap dropdowns after component renders
  useEffect(() => {
    // Add a small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      // Check if Bootstrap is available
      if (typeof window !== 'undefined' && window.bootstrap) {
        // Find all dropdown toggle elements
        const dropdownElementList = document.querySelectorAll('.dropdown-toggle');
        // Initialize each dropdown
        dropdownElementList.forEach(dropdownToggleEl => {
          try {
            new window.bootstrap.Dropdown(dropdownToggleEl);
          } catch (error) {
            console.error('Error initializing dropdown:', error);
          }
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [yearFilter]); // Re-initialize when yearFilter changes

  if (isLoading && !localData) {
    return (
      <div className='dash-section'>
        <h1 className='dash-title'>Analytics</h1>
        <div className="row">
          <div className="col-lg-4 col-md-6 mt-3">
            <div className="dash-card dash-widget">
              <Shimmer width={'100%'} height={100} />
            </div>
          </div>
          <div className="col-lg-4 col-md-6 mt-3">
            <div className="dash-card dash-widget">
              <Shimmer width={'100%'} height={100} />
            </div>
          </div>
          <div className="col-lg-4 col-md-6 mt-3">
            <div className="dash-card dash-widget">
              <Shimmer width={'100%'} height={100} />
            </div>
          </div>
          <div className="col-lg-4 col-md-6 mt-3">
            <div className="dash-card dash-widget">
              <Shimmer width={'100%'} height={100} />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 mt-4">
            <div className="dash-card">
              <div className="dash-card-heading mb-3">
                <h2 className="dash-card-title"><Shimmer width={150} height={20} /></h2>
              </div>
              <Shimmer width={'100%'} height={300} />
            </div>
          </div>
          <div className="col-lg-6 mt-4">
            <div className="dash-card mb-4 sorting-card">
              <div className="dash-card-heading mb-2">
                <h2 className="dash-card-title"><Shimmer width={120} height={20} /></h2>
              </div>
              <div className="top-items">
                <Shimmer width={'100%'} height={150} />
              </div>
            </div>
            <div className="dash-card sorting-card">
              <div className="dash-card-heading mb-2">
                <h2 className="dash-card-title"><Shimmer width={120} height={20} /></h2>
              </div>
              <div className="top-items">
                <Shimmer width={'100%'} height={150} />
              </div>
            </div>
          </div>
          <div className="col-lg-6 mt-4">
            <div className="dash-card">
              <div className="dash-card-heading mb-3">
                <h2 className="dash-card-title"><Shimmer width={120} height={20} /></h2>
              </div>
              <Shimmer width={'100%'} height={300} />
            </div>
          </div>

          <div className="col-12 mt-4">
            <div className="dash-card">
              <div className="dash-card-heading">
                <h2 className="dash-card-title">
                  <Shimmer width={100} height={20} />
                </h2>
                <div className="dash-card-heading-btn">
                  <Shimmer width={80} height={20} />
                </div>
              </div>
              <div className="table-responsive mt-3">
                <Shimmer width={'100%'} height={200} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return <div className='dash-section'><h1 className='dash-title'>Analytics</h1><div>Error fetching analytics data. Please try again later.</div></div>;
  }

  if (!isLoading && (!data || !data.reports || data.reports.length === 0)) {
    return (
      <div className='dash-section'>
        <h1 className='dash-title'>Analytics</h1>
        <div className="no-data">No analytics data available.</div>
      </div>
    );
  }

  return (
    <div className='dash-section'>
      <h1 className='dash-title'>Analytics</h1>
      <div className="row">
        <div className="col-lg-4 col-md-6 mt-3">
          <div className="dash-card dash-widget">
            <div className="dash-widget-left">
              <h1>${balance}</h1>
              <h5>Current Balance</h5>
            </div>
            <div className="dash-widget-right">
              <i class="fi fi-sr-donate"></i>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-6 mt-3">
          <div className="dash-card dash-widget">
            <div className="dash-widget-left">
              <h1>${lastMonthRevenue}</h1>
              <h5>Latest Month Revenue</h5>
            </div>
            <div className="dash-widget-right">
              <i class="fi fi-sr-hand-holding-usd"></i>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-6 mt-3">
          <div className="dash-card dash-widget">
            <div className="dash-widget-left">
              <h1>${totalPaid}</h1>
              <h5>Total Paid</h5>
            </div>
            <div className="dash-widget-right">
              <i class="fi fi-ss-piggy-bank"></i>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-md-6 mt-3">
          <div className="dash-card dash-widget">
            <div className="dash-widget-left">
              <h1>${totalInviteSplitRevenue}</h1>
              <h5>Revenue from Splits</h5>
            </div>
            <div className="dash-widget-right">
              <i class="fi fi-sr-hand-holding-usd"></i>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12 mt-4">
          <div className="dash-card">
            <div className="dash-card-heading mb-3">
              <h2 className="dash-card-title">Monthly Revenue</h2>
              <div className="date-range-selector">
                {
                  yearFilter.length > 0 && (
                    <div className="dash-form-group">
                      <label>Year</label>
                      <div className="dropdown dash-form-dropdown">
                        <button className="btn dropdown-toggle" type="button" id="yearFilterDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                          {year}
                        </button>
                        <ul className="dropdown-menu year-filter" aria-labelledby="yearFilterDropdown">
                          {
                            yearFilter.map((item, i) => (
                              <li key={i}>
                                <a
                                  className={`dropdown-item ${year === item ? 'active' : ''}`}
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleYearChange(item);
                                  }}
                                >
                                  {item}
                                </a>
                              </li>
                            ))
                          }
                        </ul>
                      </div>
                    </div>
                  )
                }
              </div>
            </div>
            {
              filteredValues.length > 0 ? (
                <div id="user-revenue-chart">
                  <ReactEcharts option={option} />
                </div>
              ) : (
                <div className="no-data">No Monthly Revenue Data for {year}</div>
              )
            }
          </div>
        </div>
        <div className="col-6 mt-4">
          <div className=''>
            <TopList 
              title="Top Countries" 
              items={topCountries} 
              labelKey="country" 
              valueKey="totalRevenue" 
              formatValue={(value) => (value || 0).toFixed(2)}
            />
          </div>
          <div className='mt-2'>
            <TopList 
              title="Top Releases" 
              items={topReleases} 
              labelKey={"release"} 
              valueKey={"totalRevenue"} 
              formatValue={(value) => (value || 0).toFixed(2)}
            />
          </div>
        </div>
        <div className="col-lg-6 mt-4">
          <TopPlatformList 
            title="Top Platforms"
            items={topPlatforms}
            chartOption={option1}
          />
        </div>

        <div className="col-12 mt-4">
          <div className="dash-card">
            <div className="dash-card-heading">
              <h2 className="dash-card-title">
                Releases
              </h2>
              <CSVLink className="dash-card-heading-btn" data={flattenReleases} filename={'releases.csv'}>
                <i className="fi fi-rr-file-export me-2"></i>
                Export CSV
              </CSVLink>
            </div>
            <ReleaseStatsTable setReleaseTracks={setReleaseTracks} setPreviewTitle={setPreviewTitle} searchQuery={searchQuery} uid={uid} previewTitle={previewTitle} />
          </div>
        </div>

        {/* Invite Splits Section */}
        {(inviteSplits?.length > 0 || inviteSplitsLoading) && <div className="col-12 mt-4">
          <div className="dash-card">
            <div className="dash-card-heading">
              <h2 className="dash-card-title">
                Revenue from Splits
              </h2>
              {inviteSplitsList.length > 0 && (
                <CSVLink className="dash-card-heading-btn" data={inviteSplitsList} filename={'invite-splits.csv'}>
                  <i className="fi fi-rr-file-export me-2"></i>
                  Export CSV
                </CSVLink>
              )}
            </div>
            {
              inviteSplitsLoading ? (
                <div className="table-responsive mt-3">
                  <Shimmer width={'100%'} height={200} />
                </div>
              ) : inviteSplitsList.length > 0 ? (
                <>
                  <div className="table-responsive mt-3">
                    <table className="table dash-table">
                      <thead>
                        <tr>
                          <td>Track Title</td>

                          <td>Track Owner</td>
                          <td>Revenue Share ($)</td>
                        </tr>
                      </thead>
                      <tbody>
                        {inviteSplitsList.map((split, index) => (
                          <tr key={split.id}>
                            <td>{split.track_title}</td>

                            <td>
                              <div className="owner-info">
                                <span className="owner-name">{split.track_owner_name}</span>
                                <span className="owner-email">{split.track_owner_email}</span>
                              </div>
                            </td>
                            <td>${split.revenue_share.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="no-data">No revenue from splits available.</div>
              )
            }
          </div>
        </div>}
        {(revenue_shared_by?.length > 0 || revenueSharedLoading) && <div className="col-12 mt-4">
          <div className="dash-card">
            <div className="dash-card-heading">
              <h2 className="dash-card-title">
                Revenue Shared For Others
              </h2>
              {revenue_shared_by?.length > 0 && (
                <CSVLink className="dash-card-heading-btn" data={inviteSplitsList} filename={'invite-splits.csv'}>
                  <i className="fi fi-rr-file-export me-2"></i>
                  Export CSV
                </CSVLink>
              )}
            </div>
            {
              revenueSharedLoading ? (
                <div className="table-responsive mt-3">
                  <Shimmer width={'100%'} height={200} />
                </div>
              ) : revenue_shared_by.length > 0 ? (
                <>
                  <div className="table-responsive mt-3">
                    <table className="table dash-table">
                      <thead>
                        <tr>
                          <td>Track Title</td>

                          <td>Revenue Shared User</td>
                          <td>Revenue Share ($)</td>
                        </tr>
                      </thead>
                      <tbody>
                        {revenue_shared_by && revenue_shared_by?.map((split, index) => (
                          <tr key={split.id}>
                            <td>{split.title}</td>
                            <td>{split.email}</td>
                            <td>${split.revenue_share.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="no-data">No revenue from splits available.</div>
              )
            }
          </div>
        </div>}
      </div>

      {/* MODAL */}
      <div className="modal fade" id="tracksModal" tabindex="-1" aria-labelledby="tracksModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-top">
                <h5>{previewTitle} Tracks</h5>
                <i className="fi fi-rr-cross" data-bs-dismiss="modal"></i>
              </div>
            </div>
            <div className="modal-body">
              <div className="table-responsive mt-3">
                <table className="table dash-table">
                  <thead>
                    <tr>
                      <td>Track Title</td>
                      <td>Streams</td>
                      <td>Revenue</td>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      releaseTracks && releaseTracks.map((track, i) => (
                        <tr key={i}>
                          <td>
                            <div className="table-item">
                              {track.title}
                            </div>
                          </td>
                          <td>
                            <div className="table-item">
                              {track.streams}
                            </div>
                          </td>
                          <td>
                            <div className="table-item">
                              ${(track.revenue * 1).toFixed(2)}
                              {
                                track.share !== 100 && <span className='track-split-text'>({track.share}% Split)</span>
                              }
                            </div>
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Analytics